import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from './Icon'
import { Popover } from './Popover'
import { cx } from '@/lib/cx'

export interface SelectOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  options: SelectOption<T>[]
  value: T
  onChange: (value: T) => void
  id?: string
  labelledBy?: string
  describedBy?: string | undefined
  invalid?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
}

/**
 * A listbox, not a `<select>`.
 *
 * Replacing a native select means re-implementing everything it gave away for
 * free, and half-doing it makes the field unusable rather than merely plain:
 * arrows and Home/End move, type-ahead jumps, Enter and Space commit, Escape
 * cancels, Tab closes, and `aria-activedescendant` keeps a screen reader on the
 * option the eye is on. The trigger is a real `<button>`, so a `<label for>`
 * still points at something labelable.
 */
export function Select<T extends string>({
  options,
  value,
  onChange,
  id,
  labelledBy,
  describedBy,
  invalid = false,
  disabled = false,
  placeholder = 'Select…',
  className,
}: Props<T>) {
  const generated = useId()
  const listId = `${id ?? generated}-listbox`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => o.value === value)))
  const wrap = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const typed = useRef({ text: '', at: 0 })

  const selected = options.find((option) => option.value === value)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  // Keeps the active option in view when the keyboard is doing the moving.
  useEffect(() => {
    if (!open) return
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  function commit(index: number) {
    const option = options[index]
    if (!option) return
    onChange(option.value)
    setOpen(false)
  }

  function openAt(index: number) {
    setActive(Math.max(0, index))
    setOpen(true)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const current = options.findIndex((o) => o.value === value)

    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        openAt(current)
      }
      return
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        return
      case 'Tab':
        setOpen(false)
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(active)
        return
      case 'ArrowDown':
        event.preventDefault()
        setActive((i) => Math.min(options.length - 1, i + 1))
        return
      case 'ArrowUp':
        event.preventDefault()
        setActive((i) => Math.max(0, i - 1))
        return
      case 'Home':
        event.preventDefault()
        setActive(0)
        return
      case 'End':
        event.preventDefault()
        setActive(options.length - 1)
        return
    }

    // Type-ahead. "b" then "l" finds "B+" then "Blood"; a pause starts over.
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const now = Date.now()
      typed.current.text = now - typed.current.at > 800 ? event.key : typed.current.text + event.key
      typed.current.at = now
      const needle = typed.current.text.toLowerCase()
      const hit = options.findIndex((o) => o.label.toLowerCase().startsWith(needle))
      if (hit >= 0) setActive(hit)
    }
  }

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        ref={trigger}
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openAt(options.findIndex((o) => o.value === value)))}
        onKeyDown={onKeyDown}
        className={cx(
          'flex min-h-field w-full items-center justify-between gap-2 rounded-md border bg-surface px-3 py-2 text-left text-base leading-snug transition duration-150 ease-standard',
          'focus:border-accent focus:ring-3 focus:ring-focus/20 focus:outline-none',
          'disabled:cursor-default disabled:border-line disabled:bg-subtle disabled:text-fg-disabled',
          invalid ? 'border-danger shadow-[inset_0_0_0_1px_var(--color-danger)]' : 'border-control',
          !disabled && 'hover:border-fg-subtle',
          selected ? 'text-fg' : 'text-fg-subtle',
          className,
        )}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <Icon
          name="chevronDown"
          size="1.125rem"
          className={cx('flex-none text-fg-subtle transition-transform duration-150 ease-standard', open && 'rotate-180')}
        />
      </button>

      <Popover
        anchor={trigger}
        open={open}
        className="max-h-64 overflow-y-auto rounded-md border border-line bg-raised p-1 shadow-lg"
      >
        <ul ref={list} id={listId} role="listbox" aria-labelledby={labelledBy}>
          {options.map((option, index) => {
            const isSelected = option.value === value
            return (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={isSelected}
                data-active={index === active}
                // Pointer, not click: the button keeps focus, so the listbox
                // never has to hand it back.
                onPointerDown={(event) => {
                  event.preventDefault()
                  commit(index)
                }}
                onPointerMove={() => setActive(index)}
                className={cx(
                  'flex min-h-touch cursor-pointer items-center justify-between gap-2 rounded-sm px-3 text-sm',
                  index === active ? 'bg-hover text-fg' : 'text-fg-muted',
                  isSelected && 'font-semibold text-fg',
                )}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <Icon name="check" size="1rem" className="flex-none text-accent" />}
              </li>
            )
          })}
        </ul>
      </Popover>
    </div>
  )
}
